import React, { useMemo } from 'react'
import { Tooltip, Whisper } from 'rsuite'
import Slider from '../../../../ui_reusable/Slider'
import { FormDatePicker } from '../../../../ui_reusable/StandardSelectPickers'
import VersatileGrid from '../../../../ui_reusable/VersatileGrid'
import {
  GridCellAlmostProminantText,
  GridCellNormalTextFaded,
} from '../../../../ui_reusable/styles/VersatileGridUtils.styles'
import {
  MainHStack,
  TuningVStack,
  SettingsTitle,
  SliderInputHStack,
  GridCellContainer,
} from './styles/BuildScenarioTuners.styles'
import { useScenarioStartDate } from '../../stores/buildScenarioStore'
import BuildScenarioRowSlider from './build_scenario_tuners/BuildScenarioRowSlider'
import BuildScenarioRowInput from './build_scenario_tuners/BuildScenarioRowInput'
import useBuildScenarioStore from '../../stores/buildScenarioStore'
import theme from '../../../../../styles/theme'

const BuildScenarioTuners = ({ scenarioId }) => {
  // Subscribe only to this specific scenario's data
  const startDate = useScenarioStartDate(scenarioId)
  const selectedDrivers = useBuildScenarioStore(
    (state) => state.selectedDrivers,
  )
  const drivers = useBuildScenarioStore((state) => state.drivers)
  const minDate = useBuildScenarioStore((state) => state.minDate)
  const maxDate = useBuildScenarioStore((state) => state.maxDate)
  const selectedModel = useBuildScenarioStore((state) => state.selectedModel)

  // Store actions
  const setScenarioStartDate = useBuildScenarioStore(
    (state) => state.setScenarioStartDate,
  )

  // Calculate if components should be disabled
  const isDisabled =
    !selectedModel ||
    selectedDrivers.length === 0 ||
    drivers.length === 0 ||
    !minDate ||
    !maxDate

  // Show tooltip only when disabled due to empty selectedDrivers specifically
  const shouldShowTooltip = selectedDrivers.length === 0

  // Transform selectedDrivers (IDs) to full driver objects
  const selectedDriverObjects = useMemo(() => {
    return selectedDrivers.map((driverId) => {
      const driver = drivers.find((d) => d.id === driverId)
      return driver
    })
  }, [selectedDrivers, drivers])

  // Convert slider value (0-100) to actual date between minDate and maxDate
  const sliderValueToDate = (sliderValue) => {
    if (!minDate || !maxDate) return new Date()
    const totalMs = new Date(maxDate).getTime() - new Date(minDate).getTime()
    const offsetMs = (sliderValue / 100) * totalMs
    return new Date(new Date(minDate).getTime() + offsetMs)
  }

  // Convert date to slider value (0-100)
  const dateToSliderValue = (date) => {
    if (!minDate || !maxDate) return 0
    const totalMs = new Date(maxDate).getTime() - new Date(minDate).getTime()
    if (totalMs <= 0) return 0
    const offsetMs = new Date(date).getTime() - new Date(minDate).getTime()
    const sliderValue = (offsetMs / totalMs) * 100
    return Math.max(0, Math.min(100, Math.round(sliderValue)))
  }

  const currentStartDateSliderValue = dateToSliderValue(startDate)

  const handleStartSliderChange = (value) => {
    const newDate = sliderValueToDate(value)
    setScenarioStartDate(scenarioId, newDate)
  }

  const handleStartInputChange = (value) => {
    if (
      value &&
      !isNaN(value.getTime()) &&
      value >= new Date(minDate) &&
      value <= new Date(maxDate)
    ) {
      setScenarioStartDate(scenarioId, value)
    }
  }

  // Helper function to check if a date should be disabled
  const isDateDisabled = (date) => {
    const checkDate = new Date(date)
    return checkDate < new Date(minDate) || checkDate > new Date(maxDate)
  }

  // Grid configuration for selectedDrivers
  const driverHeaders = [
    {
      title: 'Driver name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
    },
    {
      title: 'Source',
      key: 'source',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      center: true,
    },
    {
      title: 'Units',
      key: 'units',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      center: true,
    },
    {
      title: 'Adjust value',
      key: 'adjust_value',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      center: true,
      overrideJSX: (
        <GridCellAlmostProminantText>Adjust value</GridCellAlmostProminantText>
      ),
    },
    {
      title: 'Set value',
      key: 'set_value',
      type: 'none',
      isSortable: false,
      isSearchable: false,
      center: true,
      overrideJSX: (
        <GridCellAlmostProminantText>Set value</GridCellAlmostProminantText>
      ),
    },
  ]
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
      }}
    >
      <MainHStack>
        {(() => {
          const tuningContent = (
            <TuningVStack>
              <SettingsTitle disabled={isDisabled}>Set START</SettingsTitle>
              <SliderInputHStack>
                <Slider
                  value={currentStartDateSliderValue}
                  onChange={handleStartSliderChange}
                  min={0}
                  max={100}
                  step={1}
                  useMiddleIndicator={false}
                  disabled={isDisabled}
                  data-testid={`start-slider-${scenarioId}`}
                />
                <FormDatePicker
                  format="yyyy-MM-dd"
                  shouldDisableDate={isDateDisabled}
                  value={new Date(startDate)}
                  onChange={handleStartInputChange}
                  cleanable={false}
                  style={{ width: '10rem' }}
                  disabled={isDisabled}
                />
              </SliderInputHStack>
            </TuningVStack>
          )

          return shouldShowTooltip ? (
            <Whisper
              placement="top"
              trigger="hover"
              speaker={
                <Tooltip
                  style={{
                    backgroundColor: theme.colors.surface,
                    color: theme.colors.veryVeryFadedText,
                    outline: `1px solid ${theme.colors.veryVeryVeryFadedText}`,
                  }}
                >
                  Select one or more drivers first
                </Tooltip>
              }
            >
              {tuningContent}
            </Whisper>
          ) : (
            tuningContent
          )
        })()}
      </MainHStack>

      {/* selectedDrivers Grid */}
      {selectedDriverObjects.length > 0 && (
        <div>
          <VersatileGrid
            gridContainerStyle={{
              gridTemplateColumns: '2fr 2fr 1fr 6fr 3.7fr',
            }}
            headers={driverHeaders}
            data={selectedDriverObjects}
            paddingInGridHeader="0.0rem 0rem 0rem 0rem"
            renderRow={(rowData) => [
              <GridCellContainer key="name">
                <GridCellAlmostProminantText>
                  {rowData.name}
                </GridCellAlmostProminantText>
              </GridCellContainer>,
              <GridCellContainer key="source">
                <GridCellNormalTextFaded>
                  {rowData.source}
                </GridCellNormalTextFaded>
              </GridCellContainer>,
              <GridCellContainer key="units">
                <GridCellNormalTextFaded>
                  {rowData.units}
                </GridCellNormalTextFaded>
              </GridCellContainer>,
              <GridCellContainer key="slider">
                <BuildScenarioRowSlider
                  scenarioId={scenarioId}
                  driverId={rowData.id}
                  rowData={rowData}
                />
              </GridCellContainer>,
              <GridCellContainer key="input">
                <BuildScenarioRowInput
                  scenarioId={scenarioId}
                  driverId={rowData.id}
                  rowData={rowData}
                />
              </GridCellContainer>,
            ]}
            loading={false}
            loadingMessage="Loading drivers..."
            isSearchable={selectedDriverObjects.length > 5}
          />
        </div>
      )}
    </div>
  )
}

export default BuildScenarioTuners
